/**
 * Client-side redirect map.
 *
 * Server-side hits to a redirected URL are 301'd before SSR — by
 * `dist/_redirects` in production (Cloudflare Pages edge) or
 * `server/middleware/redirects.ts` in dev. Neither path runs on
 * `<NuxtLink>` SPA navigations, so without this guard, clicking a link
 * to a redirected URL just lands on the dead route and 404s.
 *
 * Lazy-fetches the same `redirects.json` baked into `dist/_redirects.json`
 * (prod) or served by `server/handlers/redirects-json.ts` (dev), instead
 * of shipping ~95 KB of rules in every SSR HTML response. The fetch fires
 * on the first client navigation, the result is cached on `window` for
 * the rest of the session, and the in-flight Promise is shared so
 * concurrent navigations don't refetch.
 *
 * Filename prefix `aa-` orders this before `auth.global.ts` — redirects
 * should resolve to their final destination before auth gating kicks in,
 * otherwise a redirected-and-gated path would bounce to login on the
 * source URL instead of the target.
 */
import { compile, type Matcher, type RedirectRule } from "#shared/redirectMatcher";

let matchersPromise: Promise<Matcher[]> | null = null;

function loadMatchers(): Promise<Matcher[]> {
  if (!matchersPromise) {
    matchersPromise = $fetch<RedirectRule[]>("/_redirects.json")
      .then(compile)
      .catch(() => []);
  }
  return matchersPromise;
}

export default defineNuxtRouteMiddleware(async (to, from) => {
  // Server hits already went through Nitro middleware (or `_redirects` at
  // the edge). Re-running here on SSR would duplicate work AND would also
  // pull the JSON into the SSR payload, defeating the lazy-fetch design.
  if (import.meta.server) return;
  // Initial client load — Nitro middleware just redirected us; running
  // again would be a no-op.
  if (!from || from.fullPath === to.fullPath) return;

  const matchers = await loadMatchers();
  for (const match of matchers) {
    const target = match(to.path);
    if (target) return navigateTo(target, { replace: true });
  }
});
